import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * A page's code is fetched the first time it is visited. A tab left open across
 * a deploy asks for a file that no longer exists, and the host answers with
 * index.html, so the import fails with a MIME error and the page dies with
 * "Something went wrong". The fix is a reload: the fresh index.html names the
 * current files. Reload once per page so a real outage still surfaces as an
 * error instead of a reload loop.
 */
const RELOADED_KEY = 'wz.reloadedForPage';

export const isMissingPageCode = (error: unknown): boolean =>
  error instanceof Error &&
  /dynamically imported module|module script|Unable to preload/i.test(error.message);

const alreadyReloadedFor = (path: string): boolean => {
  try {
    return sessionStorage.getItem(RELOADED_KEY) === path;
  } catch {
    return true;
  }
};

const rememberReloadFor = (path: string): void => {
  try {
    sessionStorage.setItem(RELOADED_KEY, path);
  } catch {
    // Storage unavailable: the guard above treats it as already reloaded.
  }
};

export const recoverFromMissingPageCode = (error: unknown): boolean => {
  const path = window.location.pathname;
  if (!isMissingPageCode(error) || alreadyReloadedFor(path)) return false;

  rememberReloadFor(path);
  window.location.reload();
  return true;
};

export function lazyPage<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    importer().catch((error: unknown) => {
      // Keep the loading screen up while the reload happens.
      if (recoverFromMissingPageCode(error)) return new Promise<never>(() => {});
      throw error;
    }),
  );
}
